import { ITreeItem, ITreeIndexes } from './types';

export function buildMap(flatTreeItems: Array<ITreeItem>): Map<string, ITreeIndexes> {
  const map = new Map();

  flatTreeItems.forEach((item, index, array) => {
    const { value, parentValue } = item;

    const ancestor: number[] = [];
    // if parent appears before child, then parent will in map already
    if (map.has(parentValue)) {
      const parent: ITreeIndexes = map.get(parentValue);
      ancestor.push(...parent.ancestor);
      ancestor.push(parent.index);

      parent.children = [...parent.children, index];
    } else if (parentValue) {
      // if parent appears after child, then set up parent in map with initial value
      map.set(parentValue, { index: null, ancestor: [], children: [index] });
    }

    // current value is already in map, then it must be some child's parent
    if (map.has(value)) {
      const myself: ITreeIndexes = map.get(value);
      myself.index = index;
      myself.children.forEach((child: number) => {
        const childMapValue = map.get(array[child]?.value);
        childMapValue?.ancestor.push(index);
      });
    } else {
      // ancestors has been fullfilled when searching for parent
      map.set(value, { index, ancestor, children: [] });
    }
  });

  return map;
}

function defaultFilter(item: ITreeItem, searchQuery: string) {
  const { content } = item;
  return content.toLowerCase().includes(searchQuery.toLowerCase());
}

function collectChildren(
  map: Map<string, ITreeIndexes>,
  flatTreeItems: Array<ITreeItem>,
  value: string
): Array<number> {
  const children: Array<number> = [];
  const entry: ITreeIndexes = map.get(value);
  if (entry) {
    entry.children.forEach((child: number) => {
      children.push(child);

      const childValue = flatTreeItems[child]?.value;
      children.push(...collectChildren(map, flatTreeItems, childValue));
    });
  }
  return children;
}

export function filterTreeItems(
  flatTreeItems: Array<ITreeItem>,
  map: Map<string, ITreeIndexes>,
  searchQuery: string,
  filter: (item: ITreeItem, searchQuery: string) => boolean = defaultFilter
): Array<ITreeItem> {
  const filteredTreeItemIndexes: Array<number> = [];

  flatTreeItems.forEach((item) => {
    const isMatched = filter(item, searchQuery);

    if (isMatched) {
      const { value } = item;
      const entry: ITreeIndexes = map.get(value);
      filteredTreeItemIndexes.push(entry.index);
      filteredTreeItemIndexes.push(...entry.ancestor);
      filteredTreeItemIndexes.push(...collectChildren(map, flatTreeItems, value));
    }
  });

  const set = new Set(filteredTreeItemIndexes);
  const orderedFilteredIndexes = Array.from(set).sort((a, b) => a - b);

  const filteredTreeItems = [];
  orderedFilteredIndexes.forEach((index) => {
    filteredTreeItems.push(flatTreeItems[index]);
  });

  return filteredTreeItems;
}
